import styled from "styled-components";

import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import { default as Svg12 } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronDown/12";
import {
    BILL_ICON_SIZE,
    BILL_UPSELL_ICON_SIZE,
    COLLAPSIBLE_BILL_TOOL_TIP_IMG_SIZE,
} from "@core-ui/constants/BillDetails";

const MAX_HEIGHT_EXPANDED = "1000px";

export const CollapsedBillDetailDetailsContainer = styled.button`
    position: relative;
    width: 100%;
    background: transparent;
`;

export const Heading = styled.div(() => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    transition: "all 0.4s ease",
}));

export const HeadingContainer = styled.div<ICollapsedBillDetails>({
    transform: "translateY(0px)",
    transition: "transform 0.4s ease-in",
    display: "flex",
    alignItems: "flex-start",
});

export const AmountContainer = styled.div<ICollapsedBillDetails>({
    display: "flex",
    alignContent: "center",
    alignItems: "center",
});

interface ICollapsedBillDetails {
    isExpanded?: boolean;
    currentRotation?: number;
    strikedOff?: boolean;
}
export const HeadingArrowContainer = styled.div(({ theme }) => ({
    marginLeft: `calc(${theme.spacing.xxxSmall} + ${theme.spacing.xSmall})`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    alignSelf: "flex-start",
    marginTop: `calc(${theme.spacing.xxxSmall} * -1)`,
}));

export const HeadingArrow = styled.div<ICollapsedBillDetails>(({ theme, currentRotation, isExpanded }) =>
    isExpanded
        ? {
              transform: `rotate(${currentRotation}deg)`,
              transition: "transform 0.4s ease-out",
              color: theme.colors.light.text_Med_Emphasis,
              height: "20px",
          }
        : {
              transform: `rotate(${currentRotation}deg)`,
              transition: "transform 0.4s ease-in",
              color: theme.colors.light.text_Med_Emphasis,
              height: "20px",
          },
);

export const HeadingText = styled(Text)<ICollapsedBillDetails>(({ theme, strikedOff }) => ({
    paddingLeft: 0,
    position: "relative",
    marginRight: strikedOff ? theme.spacing.xxSmall : theme.spacing.xxxSmall,
}));

export const TextWrapperCentreAlign = styled.div(({ theme }) => ({
    "& > div": {
        display: "flex",
        alignItems: "center",
        gap: `calc(${theme.spacing.xxSmall} - 2px)`,
    },
    "& img": {
        objectFit: "contain",
    },
}));

export const HeadingAmount = styled(Text)<ICollapsedBillDetails>(
    ({ theme }) => ({
        display: "inline-block",
        position: "relative",
        marginLeft: theme.spacing.xxxSmall,
    }),
    ({ theme, strikedOff }) =>
        strikedOff && {
            marginRight: theme.spacing.xxxSmall,
            marginLeft: 0,
        },
    `
        &::before {
            content: "\\20B9 ";
        }
    `,
    ({ strikedOff, theme }) =>
        strikedOff &&
        `
            &::after {
                content: '';
                border-bottom: 1px solid #414449;
                width: 100%;
                position: absolute;
                right: 0;
                left: 1px;
                top: 45%;
                filter: drop-shadow(0px 1px 0px ${theme.colors.light.background_Primary});
            }
    `,
);

export const HeadingSubText = styled(Text)<ICollapsedBillDetails>({
    position: "relative",
});

export const HeadingSubTextUpsellContainer = styled.div({
    position: "absolute",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    top: "0",
    left: "0",
    right: "0",
    bottom: "0",
    zIndex: 1,
});

export const HeadingSubTextUpsellCollapseWrapper = styled.div<ICollapsedBillDetails>(({ theme, isExpanded }) => ({
    display: "grid",
    gridTemplateRows: isExpanded ? 0 : "1fr",
    opacity: isExpanded ? 0 : 1,
    margin: isExpanded
        ? 0
        : `${theme.spacing.xSmall} calc(${theme.spacing.xSmall} * -1) calc(${theme.spacing.xxSmall} * -1) calc(${theme.spacing.xSmall} * -1)`,
    overflow: isExpanded ? "hidden" : "visible",
    pointerEvents: isExpanded ? "none" : "auto",
    transition: "grid-template-rows 0.3s ease-in-out, opacity 0.6s ease-in-out, margin 0.3s ease-in-out",
    "& > *": {
        minHeight: 0,
    },
}));

export const HeadingSubTextUpsell = styled(Text)<ICollapsedBillDetails>(
    ({ theme }) => ({
        position: "relative",
        opacity: 1,
        display: "flex",
        gap: `calc(${theme.spacing.xxxSmall} + ${theme.spacing.xxSmall})`,
        backgroundColor: theme.colors[theme.colorMode].border_Tertiary,
        padding: `${theme.spacing.xSmall} ${theme.spacing.small}`,
        borderRadius: theme.cornerRadius.small,
        alignItems: "center",
    }),
    ({ theme }) => `
    &::after {
        /* arrow */
        content: "";
        position: absolute;
        top: calc(0px - (${theme.spacing.xxxSmall} + ${theme.spacing.xxSmall}));
        left: calc(${theme.spacing.medium} + ${BILL_ICON_SIZE} + ${theme.spacing.small});
        height: ${theme.spacing.small};
        width: ${theme.spacing.small};
        background: ${theme.colors[theme.colorMode].border_Tertiary};
        clip-path: polygon(0% 0%, 100% 100%, 0% 100%);
        transform: rotate(135deg);
        border-radius: 0 0 0 4px;
    }
    & div {
        display: flex;
    }
    & .tool-tip-image {
        height: ${COLLAPSIBLE_BILL_TOOL_TIP_IMG_SIZE};
        margin: 0 ${theme.spacing.xxxSmall};
    }

`,
    ({ isExpanded }) =>
        isExpanded && {
            opacity: 0,
            transition: "opacity 0.3s ease-in-out, all 0.3s ease-in-out",
        },
);

export const Content = styled.div<ICollapsedBillDetails>(
    {
        maxHeight: 0,
        overflow: "hidden",
        transition: `max-height 0.6s cubic-bezier(0.4, 0.0, 0.2, 1), opacity 0.6s ease-in-out`,
        opacity: 0,
    },
    ({ isExpanded }) =>
        isExpanded && {
            maxHeight: MAX_HEIGHT_EXPANDED,
            opacity: 1,
            overflow: "unset",
            transition: `max-height 0.6s cubic-bezier(0.4, 0.0, 0.2, 1), opacity 0.6s ease-in-out`,
        },
);

export const Separator = styled.div(({ theme }) => ({
    height: "1px",
    marginTop: theme.spacing.medium,
    marginBottom: `calc(${theme.spacing.small} + ${theme.spacing.xxxSmall})`,
    position: "relative",

    "::before": {
        content: "''",
        position: "absolute",
        top: "0",
        right: `calc(${theme.spacing.medium} * -1)`,
        bottom: "0",
        left: `calc(${theme.spacing.medium} * -1)`,
        backgroundColor: theme.colors[theme.colorMode].border_Tertiary,
    },
}));

export const ChevronRight = styled(Svg12)(({ theme }) => ({
    fill: "unset",
    marginTop: theme.spacing.xxSmall,
}));

export const IconWrapper = styled.div(({ theme }) => {
    return {
        width: BILL_ICON_SIZE,
        height: BILL_ICON_SIZE,
        marginRight: theme.spacing.small,
    };
});

export const TooltipIconWrapper = styled.div(() => {
    return {
        width: BILL_UPSELL_ICON_SIZE,
        height: BILL_UPSELL_ICON_SIZE,
    };
});
